/**
 * Signed cookies for data.mdf.lu (the Digital Twin files). The platform API
 * sets them (POST /v1/data-access, HttpOnly, valid 12 h); this module asks for
 * them before the first data request, renews them before they run out, and
 * after a 403 from data.mdf.lu renews once and tries again.
 *
 * No app imports here, so the tests run it with a fake fetch and clock.
 */

/** Why map data could not be loaded; DataAccessNotice shows a message for it. */
export type DataAccessProblem = "signed-out" | "no-access" | "unavailable";

type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;
export type DataFetchInit = FetchInit & { optional?: boolean };

export interface DataAccessConfig {
  /** POST /v1/data-access through the platform API (adds the access token). */
  issue: () => Promise<Response>;
  fetch: typeof fetch;
  /** Where the data is read from: https://data.mdf.lu, or the dev proxy. */
  dataBaseUrl: () => string;
  /** The public address the configured file URLs use. */
  publicBaseUrl: string;
  now?: () => number;
}

/** Ask for new cookies this long before the current ones run out. */
export const RENEW_BEFORE_MS = 10 * 60 * 1000;
/** When the API answer has no usable expiry: ask again after an hour. */
const FALLBACK_VALID_MS = 60 * 60 * 1000;

const problemFor = (status: number): DataAccessProblem =>
  status === 401 ? "signed-out" : status === 403 ? "no-access" : "unavailable";

const trimSlash = (url: string) => url.replace(/\/+$/, "");

export function createDataAccess(config: DataAccessConfig) {
  const now = config.now ?? Date.now;
  let validUntil = 0;
  let renewing: Promise<boolean> | null = null;
  /** Why the last renewal failed; shown only if data.mdf.lu then refuses. */
  let lastFailure: DataAccessProblem | null = null;
  let problem: DataAccessProblem | null = null;
  const listeners = new Set<() => void>();

  const setProblem = (next: DataAccessProblem | null) => {
    if (next === problem) return;
    problem = next;
    listeners.forEach((listener) => listener());
  };

  /** New cookies from the API; false when it refused or could not be reached. */
  const renew = (): Promise<boolean> => {
    renewing ??= (async () => {
      try {
        const response = await config.issue();
        if (!response.ok) {
          validUntil = 0;
          lastFailure = problemFor(response.status);
          return false;
        }
        const body = (await response.json().catch(() => ({}))) as { expiresAt?: unknown };
        const until = typeof body.expiresAt === "string" ? Date.parse(body.expiresAt) : Number.NaN;
        validUntil = Number.isFinite(until) ? until : now() + FALLBACK_VALID_MS;
        lastFailure = null;
        return true;
      } catch {
        validUntil = 0;
        lastFailure = "unavailable";
        return false;
      } finally {
        renewing = null;
      }
    })();
    return renewing;
  };

  /** The configured file URL, pointed at the dev proxy when one is set. */
  const resolve = (url: string): string => {
    const publicBase = trimSlash(config.publicBaseUrl);
    const base = trimSlash(config.dataBaseUrl());
    return base !== publicBase && url.startsWith(`${publicBase}/`)
      ? `${base}${url.slice(publicBase.length)}`
      : url;
  };

  const isDataUrl = (url: string) => url.startsWith(`${trimSlash(config.dataBaseUrl())}/`);

  /**
   * fetch() for data files. Requests to data.mdf.lu carry the cookies; any
   * other URL (e.g. local files in development) is fetched as is.
   *
   * `optional`: a file that may not be published yet (sim/). data.mdf.lu
   * answers 403, not 404, for a missing file, so a 403 or 404 then just means
   * "not there": no renewal, no retry and no message.
   */
  async function dataFetch(
    url: string,
    { optional = false, ...init }: DataFetchInit = {},
  ): Promise<Response> {
    const target = resolve(url);
    if (!isDataUrl(target)) return config.fetch(target, init);

    let renewed = false;
    if (validUntil - now() < RENEW_BEFORE_MS) {
      // A failure is not fatal here: data.mdf.lu may not need the cookie (yet).
      await renew();
      renewed = true;
    }
    const send = () => config.fetch(target, { ...init, credentials: "include" });
    let response = await send();
    if (optional && (response.status === 403 || response.status === 404)) return response;
    // Cookies expired while the tab slept, or the key was renewed: once more.
    if (response.status === 403 && !renewed && (await renew())) response = await send();

    if (response.status === 403) setProblem(lastFailure ?? "unavailable");
    else if (response.ok) setProblem(null);
    return response;
  }

  return {
    dataFetch,
    /** The current problem, or null when data loads fine. */
    getProblem: () => problem,
    onProblemChange(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** Forget the cookies (signed out); the next request asks again. */
    reset() {
      validUntil = 0;
      lastFailure = null;
      setProblem(null);
    },
  };
}

export type DataAccess = ReturnType<typeof createDataAccess>;
